<template>
  <full-width-spinner v-if="busy" />
  <ion-tabs
    v-else
    class="bridge-detail"
    :style="correctedStyling"
  >
    <ion-content>
      <ion-card>
        <ion-card-header>
          <ion-item
            class="header"
            lines="none"
          >
            <h1>
              Bridge: {{ serialNumber }}
              <ion-icon
                v-if="bridge.is_virtual"
                :src="i.cloudOutline"
              />
            </h1>

            <ion-button
              v-if="isAdmin"
              slot="end"
              @click="confirmAndPinToRelease"
            >
              <ion-spinner v-if="pinningToRelease" />
              <template v-else>
                Pin to release
              </template>
            </ion-button>

            <ion-button
              v-if="isAdmin"
              slot="end"
              @click="pinToVersionModal"
            >
              <ion-spinner v-if="fetchingCommits" />
              <template v-else>
                Pin to version
              </template>
            </ion-button>

            <ion-button
              v-if="isAdmin"
              slot="end"
              @click="fetchAndShowSmartboxToken"
            >
              <ion-spinner v-if="fetchingToken" />
              <template v-else>
                Smartbox token
              </template>
            </ion-button>

            <ion-button
              slot="end"
              @click="confirmAndRestart"
            >
              <ion-spinner v-if="restarting" />
              <template v-else>
                Restart
              </template>
            </ion-button>

            <ion-button
              v-if="isAdmin"
              slot="end"
              @click="confirmAndReboot"
            >
              <ion-spinner v-if="rebooting" />
              <template v-else>
                Reboot
              </template>
            </ion-button>

            <ion-button
              v-if="isAdmin"
              slot="end"
              @click="confirmAndDelete"
            >
              <ion-spinner v-if="deleting" />
              <template v-else>
                Delete
              </template>
            </ion-button>
          </ion-item>

          <ion-tab-bar>
            <ion-tab-button
              tab="info"
              @click="hideDetails"
            >
              Info
            </ion-tab-button>

            <ion-tab-button
              tab="rooms"
              @click="hideDetails"
            >
              Rooms
            </ion-tab-button>

            <ion-tab-button
              tab="devices"
              @click="hideDetails"
            >
              Devices
            </ion-tab-button>

            <ion-tab-button
              tab="climate"
              @click="hideDetails"
            >
              Climate api
            </ion-tab-button>

            <ion-tab-button
              tab="auth"
              @click="hideDetails"
            >
              Authentication
            </ion-tab-button>

            <ion-tab-button
              tab="users"
              @click="hideDetails"
            >
              Users
            </ion-tab-button>
          </ion-tab-bar>
        </ion-card-header>

        <ion-card-content>
          <template>
            <ion-tab tab="info">
              <info-tab
                :pinning-to-release="pinningToRelease"
                @confirmAndPinToRelease="confirmAndPinToRelease"
              />
            </ion-tab>

            <ion-tab tab="rooms">
              <rooms-tab />
            </ion-tab>

            <ion-tab tab="devices">
              <devices-tab />
            </ion-tab>

            <ion-tab tab="climate">
              <external-api-clients-tab />
            </ion-tab>

            <ion-tab tab="auth">
              <auth-tab />
            </ion-tab>

            <ion-tab tab="users">
              <users-tab />
            </ion-tab>
          </template>
        </ion-card-content>
      </ion-card>
    </ion-content>
  </ion-tabs>
</template>

<script>
import { mapGetters } from 'vuex'
import { alertController, modalController } from '@modus/ionic-vue'

import InfoTab from './InfoTab'
import RoomsTab from './RoomsTab'
import DevicesTab from './DevicesTab'
import ExternalApiClientsTab from './ExternalApiClientsTab'
import AuthTab from './AuthTab'
import UsersTab from './UsersTab'
import SmartboxToken from './SmartboxToken'
import PinToVersion from './PinToVersion'

import FullWidthSpinner from '../../common/FullWidthSpinner'

export default {
  components: {
    InfoTab,
    RoomsTab,
    DevicesTab,
    FullWidthSpinner,
    ExternalApiClientsTab,
    AuthTab,
    UsersTab
  },
  data() {
    return {
      busy: true,
      restarting: false,
      rebooting: false,
      deleting: false,
      fetchingToken: false,
      fetchingCommits: false,
      pinningToRelease: false,
      serialNumber: this.$route.params.serialNumber
    }
  },
  computed: {
    ...mapGetters([
      'getBridgeBySerial',
      'isAdmin'
    ]),
    correctedStyling () {
      if (navigator.userAgent.search('Firefox') === -1 || process.env.NODE_ENV !== 'production' ) return {}
      return {
        paddingTop: '88px',
      }
    },
    bridge() {
      return this.$store.getters.getBridgeBySerial(this.serialNumber)
        .bridge
    }
  },
  created() {
    const serialNumber = this.serialNumber

    this.$store.dispatch('fetchBridge', { serialNumber })
        .finally(() => this.busy = false)
  },
  methods: {
    hideDetails() {
      this.$store.dispatch('updateDetailTerms', {})
    },
    async confirmAndRestart() {
      const serialNumber = this.serialNumber
      const self = this

      alertController
        .create({
          header: `Restarting '${serialNumber}'`,
          message: 'This will restart all services on the bridge.<br><br>Are you sure?',
          buttons: [
            {
              text: 'No',
              role: 'cancel'
            },
            {
              text: 'Yes',
              handler() {
                self.restart()
              }
            }
          ],
        })
        .then(a => a.present())
    },
    async restart() {
      const serialNumber = this.serialNumber

      this.restarting = true

      this.$store.dispatch('restartBridge', { serialNumber })
        .finally(() => this.restarting = false)
    },
    async confirmAndReboot() {
      const serialNumber = this.serialNumber
      const self = this

      alertController
          .create({
            header: `Reboot '${serialNumber}'`,
            message: 'Are you sure?',
            buttons: [
              {
                text: 'No',
                role: 'cancel'
              },
              {
                text: 'Yes',
                handler() {
                  self.reboot()
                }
              }
            ],
          })
          .then(a => a.present())
    },
    async reboot() {
      const serialNumber = this.serialNumber

      this.rebooting = true

      this.$store.dispatch('rebootBridge', { serialNumber })
          .finally(() => this.rebooting = false)
    },
    async confirmAndDelete() {
      const serialNumber = this.serialNumber
      const self = this

      alertController
          .create({
            header: `Delete '${serialNumber}'`,
            message: 'Are you sure?',
            buttons: [
              {
                text: 'No',
                role: 'cancel'
              },
              {
                text: 'Yes',
                handler() {
                  self.delete()
                }
              }
            ],
          })
          .then(a => a.present())
    },
    async delete() {
      const serialNumber = this.serialNumber

      this.deleting = true

      this.$store.dispatch('deleteBridge', { serialNumber })
          .finally(() => {
            this.deleting = false
            this.$router.push({name: 'bridges'})
          })
    },
    async fetchAndShowSmartboxToken() {
      const serialNumber = this.serialNumber

      this.fetchingToken = true

      this.$store.dispatch('fetchSmartboxToken', { serialNumber })
        .then(token => this.showSmartboxToken(token))
        .finally(() => this.fetchingToken = false)
    },
    async showSmartboxToken(token) {
      const modal = await modalController
        .create({
          component: SmartboxToken,
          componentProps: {
            propsData: {
              token
            }
          },
        })

      modal.present()
    },
    async pinToVersionModal() {
      const modal = await modalController
        .create({
          component: PinToVersion,
          componentProps: {
            propsData: {
              vuetify: this.$vuetify
            }
          }
        })

      this.fetchingCommits = true

      this.$store.dispatch('fetchCommits', { project: 1 })
        .finally(() => this.fetchingCommits = false)
        .then(() => modal.present())
    },
    async confirmAndPinToRelease() {
      this.pinningToRelease = true

      const releaseTrack = this.bridge.release_track
      const serialNumber = this.serialNumber
      const latest = await this.$store.dispatch('fetchLatestRelease', { releaseTrack })
        .catch(() => this.pinningToRelease = false)
      const version = latest.commit.version
      const self = this

      alertController
        .create({
          header: `Pinning '${serialNumber}'`,
          message: `You're about to pin ${serialNumber} to ${releaseTrack} (${version}). Are you sure?<br><br>
          If the Bridge is offline, it will be updated as soon as it comes online.`,
          buttons: [
            {
              text: 'No',
              role: 'cancel',
              handler() {
                self.pinningToRelease = false
              }
            },
            {
              text: 'Yes',
              handler() {
                self.pinToVersion(version)
              }
            }
          ],
        })
        .then(a => a.present())
    },
    pinToVersion(version) {
      const serialNumber = this.serialNumber

      this.$store.dispatch('pinToVersion', { serialNumber, version })
        .finally(() => this.pinningToRelease = false)
    }
  },
}
</script>

<style scoped>
.detail-page {
  height:100%;
}

.header {
  --padding-start:0;
  margin-bottom:20px;
}

ion-card-header ion-spinner {
  width:12px;
}
</style>
